import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ArrowUpRight } from "lucide-react";
import { useRole } from "../context/RoleContext";
import { listOwnerProjects, getOwnerDashboard } from "../lib/ownerPortal";
import { WaitingOnPill, stageBlocker, relativeDate } from "../lib/ownerUi";
import { formatPriceCompact, formatPriceFull } from "../lib/format";
import StatusPill from "../components/StatusPill";

/* ──────────────────────────────────────────────────────────────
   Stage-age + next-step inference (data is already on the project)
   ────────────────────────────────────────────────────────────── */

const STAGE_ORDER = [
  "Selection", "Consultation", "Purchase", "Expedited Review",
  "Build", "Construction Inspection", "Complete",
];

function stageAgeDays(p) {
  const entry = (p.stage_history || []).find(
    (h) => h.stage === p.current_stage,
  );
  const at = entry?.at || p.updated_at;
  if (!at) return null;
  const days = Math.floor((Date.now() - new Date(at).getTime()) / 86400000);
  return days > 0 ? days : null;
}

function stageAgeLabel(days) {
  if (days == null) return null;
  if (days < 3) return null; // Don't warn for fresh transitions
  if (days < 14) return { label: `${days} days in stage`, warn: false };
  if (days < 30) return {
    label: `${Math.floor(days / 7)} weeks in stage`,
    warn: false,
  };
  return {
    label: `Stuck ${Math.floor(days / 7)} weeks in stage`,
    warn: true,
  };
}

function nextStep(p) {
  // What the owner can do next, inline on the row. All hrefs land on
  // the project detail page (where the actual interaction lives).
  const stage = (p.current_stage || "").toLowerCase();
  const href = `/owner/projects/${p.id}`;
  if (stage === "selection")
    return { label: "Continue setup", href };
  if (stage === "consultation")
    return { label: "Open consultation", href };
  if (stage === "purchase") {
    const sqft = (p.design?.sqft || 0);
    const amt = 2500 + (Number.isFinite(sqft) ? Number(sqft) : 0);
    return { label: `Pay ${formatPriceCompact(amt)} · plans`, href, primary: true };
  }
  if (stage === "expedited review")
    return { label: "View permit status", href };
  if (stage === "build")
    return { label: "View site updates", href };
  if (stage === "construction inspection")
    return { label: "Schedule inspection", href };
  if (stage === "complete")
    return { label: "View final record", href };
  return { label: "Open project", href };
}

/* ──────────────────────────────────────────────────────────────
   Page
   ────────────────────────────────────────────────────────────── */

const FILTERS = [
  { id: "all", label: "All projects" },
  { id: "action", label: "Needs me" },
  { id: "active", label: "In motion" },
  { id: "complete", label: "Complete" },
];

export default function OwnerProjects() {
  const { user } = useRole();
  const [data, setData] = useState(null);
  const [invested, setInvested] = useState(0);
  const [filter, setFilter] = useState("all");
  const [showCompleted, setShowCompleted] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    Promise.all([
      listOwnerProjects(user.id),
      getOwnerDashboard(user.id).catch(() => null),
    ]).then(([projects, dashboard]) => {
      if (!alive) return;
      setData(projects);
      setInvested(dashboard?.finance?.lifetime_total || 0);
    });
    return () => {
      alive = false;
    };
  }, [user?.id]);

  /* ─── derived: portfolio + filtered groups ─── */
  const portfolio = useMemo(() => {
    if (!data) return null;
    const all = [
      ...(data.in_progress || []),
      ...(data.awaiting_permit || []),
      ...(data.completed || []),
    ];
    const committed = all.reduce(
      (sum, p) => sum + ((p.costs && p.costs.total_cost) || 0),
      0,
    );
    const neighborhoods = new Set(
      all.map((p) => p.parcel?.neighborhood).filter(Boolean),
    );
    // Stage distribution for the mini bar
    const stageCounts = STAGE_ORDER.reduce((acc, s) => {
      acc[s] = all.filter(
        (p) => (p.current_stage || "").toLowerCase() === s.toLowerCase(),
      ).length;
      return acc;
    }, {});
    return {
      total: all.length,
      committed,
      neighborhoods: neighborhoods.size,
      stageCounts,
    };
  }, [data]);

  const groups = useMemo(() => {
    if (!data) return { action: [], active: [], complete: [] };
    const in_progress = data.in_progress || [];
    const awaiting = data.awaiting_permit || [];
    const completed = data.completed || [];
    // "Needs me" = anything where blocker.tone === "owner"
    const allActive = [...in_progress, ...awaiting];
    const action = allActive.filter(
      (p) => stageBlocker(p.current_stage).tone === "owner",
    );
    const active = allActive.filter(
      (p) => stageBlocker(p.current_stage).tone !== "owner",
    );
    return { action, active, complete: completed };
  }, [data]);

  if (!data) {
    return (
      <div
        style={{ color: "#6C6B85", fontStyle: "italic" }}
        data-testid="owner-projects-loading"
      >
        Loading projects…
      </div>
    );
  }

  const visibleAction = (filter === "all" || filter === "action")
    ? groups.action : [];
  const visibleActive = (filter === "all" || filter === "active")
    ? groups.active : [];
  const visibleComplete = (filter === "all" || filter === "complete")
    ? groups.complete : [];

  return (
    <section className="animate-fade-in-up" data-testid="owner-projects">
      {/* ─── Portfolio header ─── */}
      <PortfolioHeader
        portfolio={portfolio}
        invested={invested}
      />

      {/* ─── Filter chips ─── */}
      <div
        className="mt-8 flex flex-wrap items-center"
        style={{ gap: 8 }}
        data-testid="owner-projects-filters"
      >
        {FILTERS.map((f) => {
          const count = {
            all: groups.action.length + groups.active.length + groups.complete.length,
            action: groups.action.length,
            active: groups.active.length,
            complete: groups.complete.length,
          }[f.id];
          return (
            <FilterChip
              key={f.id}
              label={`${f.label} (${count})`}
              active={filter === f.id}
              disabled={count === 0 && f.id !== "all"}
              onClick={() => setFilter(f.id)}
              testid={`owner-projects-filter-${f.id}`}
            />
          );
        })}
      </div>

      {/* ─── Needs your attention (hero) ─── */}
      {visibleAction.length > 0 && (
        <Group
          marker="§ A"
          eyebrow="Needs your attention"
          title={
            visibleAction.length === 1
              ? "1 project is waiting on you."
              : `${visibleAction.length} projects are waiting on you.`
          }
          dek="Each one needs your decision, signature, or payment before it can move."
          rows={visibleAction}
          testId="owner-projects-action"
          tone="hero"
        />
      )}

      {/* ─── In motion ─── */}
      {visibleActive.length > 0 && (
        <Group
          marker="§ B"
          eyebrow="In motion"
          title="Out of your hands."
          dek="Waiting on the architect or city. You'll be notified when they need you again."
          rows={visibleActive}
          testId="owner-projects-active"
        />
      )}

      {/* ─── Completed (collapsed by default) ─── */}
      {visibleComplete.length > 0 && (
        <div className="mt-10" data-testid="owner-projects-completed">
          <button
            type="button"
            onClick={() => setShowCompleted((v) => !v)}
            data-testid="owner-projects-completed-toggle"
            className="inline-flex items-center"
            style={{
              gap: 8,
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 12,
              letterSpacing: "0.10em",
              textTransform: "uppercase",
              color: "#6C6B85",
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
          >
            <span
              aria-hidden
              style={{
                display: "inline-block",
                width: 22,
                height: 2,
                background: "#9AA0C0",
              }}
            />
            {showCompleted ? "Hide" : "Show"} {visibleComplete.length} completed
            <span aria-hidden>{showCompleted ? "▴" : "▾"}</span>
          </button>
          {showCompleted && (
            <div className="mt-4 space-y-3">
              {visibleComplete.map((p) => (
                <ProjectRow key={p.id} p={p} />
              ))}
            </div>
          )}
        </div>
      )}

      {visibleAction.length === 0
        && visibleActive.length === 0
        && visibleComplete.length === 0 && (
        <EmptyState />
      )}
    </section>
  );
}

/* ──────────────── Portfolio header ──────────────── */

function PortfolioHeader({ portfolio, invested }) {
  if (!portfolio) return null;
  const { total, committed, neighborhoods, stageCounts } = portfolio;
  const stages = STAGE_ORDER.filter((s) => stageCounts[s] > 0);
  return (
    <header data-testid="owner-projects-portfolio">
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 28,
            height: 2,
            background: "#B14E2F",
          }}
        />
        <span className="wn-label" style={{ color: "#8F3C23" }}>
          Owner portfolio
        </span>
      </div>
      <h1
        className="mt-3"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: "clamp(28px, 4vw, 40px)",
          fontWeight: 500,
          color: "#1B1F4B",
          lineHeight: 1.1,
          letterSpacing: "-0.01em",
          margin: 0,
        }}
        data-testid="owner-projects-h1"
      >
        Your projects.
      </h1>
      <p
        className="mt-2"
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          color: "#6C6B85",
          fontSize: 16,
          lineHeight: 1.5,
          maxWidth: 720,
          margin: 0,
        }}
      >
        From Selection through Complete — every build you have in motion,
        with the next move you need to make.
      </p>

      {/* Stat strip */}
      <div
        className="mt-6 grid grid-cols-2 md:grid-cols-4"
        style={{
          gap: 0,
          borderTop: "1px solid #D9D8E4",
          borderBottom: "1px solid #D9D8E4",
        }}
      >
        <Stat label="Projects" value={total} />
        <Stat
          label="Committed"
          value={formatPriceCompact(committed)}
          accent
        />
        <Stat
          label="Invested to date"
          value={formatPriceCompact(invested)}
        />
        <Stat
          label="Neighborhoods"
          value={neighborhoods}
        />
      </div>

      {/* Stage distribution bar */}
      {stages.length > 0 && (
        <div className="mt-6">
          <div
            className="wn-label"
            style={{ color: "#6C6B85", marginBottom: 8 }}
          >
            Distribution by stage
          </div>
          <div
            className="flex"
            style={{
              height: 8,
              borderRadius: 999,
              overflow: "hidden",
              background: "#ECEBF1",
            }}
          >
            {stages.map((s, i) => {
              const tone = stageBlocker(s);
              return (
                <span
                  key={s}
                  title={`${s} · ${stageCounts[s]}`}
                  style={{
                    flex: stageCounts[s],
                    background: tone.color,
                    borderRight: i < stages.length - 1
                      ? "1px solid #FBFAFD" : "none",
                    transition: "flex 240ms ease",
                  }}
                />
              );
            })}
          </div>
          <div
            className="mt-2 flex flex-wrap"
            style={{ gap: 16, fontSize: 12 }}
          >
            {stages.map((s) => {
              const tone = stageBlocker(s);
              return (
                <span
                  key={s}
                  className="inline-flex items-center"
                  style={{ gap: 6, color: "#6C6B85" }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 8, height: 8, borderRadius: 999,
                      background: tone.color,
                    }}
                  />
                  <span
                    className="tabular-nums"
                    style={{
                      fontFamily: '"Nunito Sans", sans-serif',
                      fontWeight: 700,
                      color: "#272636",
                    }}
                  >
                    {stageCounts[s]}
                  </span>
                  {s}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div
      style={{
        padding: "16px 18px",
        borderRight: "1px solid #D9D8E4",
      }}
    >
      <div className="wn-label" style={{ color: "#6C6B85" }}>
        {label}
      </div>
      <div
        className="tabular-nums mt-1"
        style={{
          fontFamily: '"Nunito Sans", sans-serif',
          fontWeight: 800,
          fontSize: 26,
          color: accent ? "#8F3C23" : "#1B1F4B",
          letterSpacing: "-0.005em",
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
    </div>
  );
}

/* ──────────────── Filter chip ──────────────── */

function FilterChip({ label, active, disabled, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      data-testid={testid}
      disabled={disabled}
      style={{
        fontFamily: '"Nunito Sans", sans-serif',
        fontWeight: 700,
        fontSize: 12,
        letterSpacing: "0.04em",
        padding: "6px 12px",
        borderRadius: 999,
        border: active ? "1px solid #8F3C23" : "1px solid #D9D8E4",
        background: active ? "#8F3C23" : "transparent",
        color: active ? "#FBFAFD" : (disabled ? "#9AA0C0" : "#1B1F4B"),
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        transition: "background-color 160ms ease, border-color 160ms ease",
      }}
    >
      {label}
    </button>
  );
}

/* ──────────────── Group ──────────────── */

function Group({ marker, eyebrow, title, dek, rows, testId, tone }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="mt-10" data-testid={testId}>
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 22,
            height: 2,
            background: tone === "hero" ? "#B14E2F" : "#9AA0C0",
          }}
        />
        <span
          className="wn-label"
          style={{ color: tone === "hero" ? "#8F3C23" : "#6C6B85" }}
        >
          {marker} · {eyebrow}
        </span>
      </div>
      <h2
        className="mt-2"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 500,
          fontSize: "clamp(22px, 2.4vw, 28px)",
          color: "#1B1F4B",
          lineHeight: 1.2,
          margin: 0,
        }}
      >
        {title}
      </h2>
      {dek && (
        <p
          className="mt-1"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 14,
            lineHeight: 1.5,
            margin: 0,
            maxWidth: 620,
          }}
        >
          {dek}
        </p>
      )}
      <div className="mt-5 space-y-3">
        {rows.map((p) => (
          <ProjectRow key={p.id} p={p} hero={tone === "hero"} />
        ))}
      </div>
    </div>
  );
}

/* ──────────────── ProjectRow ──────────────── */

function ProjectRow({ p, hero }) {
  const blocker = stageBlocker(p.current_stage);
  const isComplete = (p.current_stage || "").toLowerCase() === "complete";
  // Suppress stage-age "stuck" warning on Complete (the project is done,
  // not stuck). Show "Completed {date}" instead, sourced from history.
  const completedAt = (p.stage_history || []).find(
    (h) => (h.stage || "").toLowerCase() === "complete",
  )?.at;
  const age = isComplete ? null : stageAgeLabel(stageAgeDays(p));
  const total = (p.costs && p.costs.total_cost) || 0;
  const cta = nextStep(p);

  return (
    <Link
      to={`/owner/projects/${p.id}`}
      data-testid={`owner-project-row-${p.id}`}
      style={{
        display: "block",
        border: "1px solid #D9D8E4",
        borderLeft: `3px solid ${blocker.color}`,
        borderRadius: 4,
        background: hero ? "#FFF9F2" : "#FBFAFD",
        padding: "18px 20px",
        textDecoration: "none",
        color: "inherit",
        transition: "border-color 200ms ease, box-shadow 200ms ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderTopColor = "#B14E2F";
        e.currentTarget.style.borderRightColor = "#B14E2F";
        e.currentTarget.style.borderBottomColor = "#B14E2F";
        e.currentTarget.style.boxShadow =
          "0 1px 0 rgba(27,31,75,0.04), 0 12px 24px -10px rgba(27,31,75,0.12)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderTopColor = "#D9D8E4";
        e.currentTarget.style.borderRightColor = "#D9D8E4";
        e.currentTarget.style.borderBottomColor = "#D9D8E4";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div
        className="grid items-center"
        style={{
          gridTemplateColumns: "minmax(0, 1fr) auto",
          gap: 24,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            className="flex items-center flex-wrap"
            style={{ gap: 10 }}
          >
            <WaitingOnPill
              stage={p.current_stage}
              testId={`owner-project-blocker-${p.id}`}
            />
            <StatusPill
              value={p.current_stage}
              variant="soft"
              testId={`owner-project-stage-${p.id}`}
            />
            {age && (
              <span
                className="wn-label tabular-nums"
                style={{
                  color: age.warn ? "#B14E2F" : "#6C6B85",
                  letterSpacing: "0.04em",
                  textTransform: "none",
                  fontStyle: "italic",
                  fontFamily: '"Newsreader", Georgia, serif',
                  fontWeight: 400,
                  fontSize: 12.5,
                }}
                data-testid={`owner-project-age-${p.id}`}
              >
                · {age.label}
              </span>
            )}
            {isComplete && completedAt && (
              <span
                className="tabular-nums"
                style={{
                  color: "#4F6149",
                  letterSpacing: "0.04em",
                  textTransform: "none",
                  fontStyle: "italic",
                  fontFamily: '"Newsreader", Georgia, serif',
                  fontWeight: 400,
                  fontSize: 12.5,
                }}
                data-testid={`owner-project-completed-${p.id}`}
              >
                · Completed {relativeDate(completedAt)}
              </span>
            )}
          </div>
          <div
            className="mt-2"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 500,
              fontSize: 20,
              color: "#1B1F4B",
              lineHeight: 1.2,
            }}
          >
            {p.design?.name || p.id}
          </div>
          <div
            className="mt-1"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 13.5,
              lineHeight: 1.45,
            }}
          >
            {p.parcel?.address || "—"} · {p.architect?.firm || "—"}
          </div>
          <div
            className="tabular-nums mt-2"
            style={{
              color: "#6C6B85",
              fontSize: 12,
              fontFamily: '"Nunito Sans", sans-serif',
            }}
          >
            Total{" "}
            <span style={{ color: "#272636", fontWeight: 700 }}>
              {total ? formatPriceFull(total) : "—"}
            </span>
            {p.updated_at && (
              <>
                {" · "}Updated {relativeDate(p.updated_at)}
              </>
            )}
          </div>
        </div>

        {/* Inline next-step CTA */}
        <div
          className="flex items-center"
          style={{ gap: 16, flexShrink: 0 }}
        >
          <span
            data-testid={`owner-project-cta-${p.id}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 13,
              padding: cta.primary ? "10px 16px" : "8px 14px",
              borderRadius: cta.primary ? 4 : 999,
              border: cta.primary ? "none" : "1px solid #D9D8E4",
              background: cta.primary ? "#1B1F4B" : "transparent",
              color: cta.primary ? "#FBFAFD" : "#1B1F4B",
              whiteSpace: "nowrap",
            }}
          >
            {cta.label}
            <ArrowUpRight size={13} />
          </span>
          <ChevronRight size={16} color="#9AA0C0" />
        </div>
      </div>
    </Link>
  );
}

/* ──────────────── Empty state ──────────────── */

function EmptyState() {
  return (
    <div
      className="mt-10"
      data-testid="owner-projects-empty"
      style={{
        border: "1px solid #D9D8E4",
        borderRadius: 4,
        backgroundColor: "#FBFAFD",
        padding: "28px 28px",
        display: "grid",
        gridTemplateColumns: "1fr auto",
        alignItems: "center",
        gap: 20,
      }}
    >
      <div>
        <div
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 500,
            fontSize: 22,
            color: "#1B1F4B",
            lineHeight: 1.2,
          }}
        >
          Nothing here under this filter.
        </div>
        <div
          className="mt-2"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 14,
          }}
        >
          Try a different filter, or browse the marketplace to start a new project.
        </div>
      </div>
      <Link
        to="/marketplace"
        className="wn-btn wn-btn-accent inline-flex items-center"
        style={{ gap: 8 }}
      >
        Browse marketplace
        <ArrowUpRight size={14} />
      </Link>
    </div>
  );
}
